import { formatReferenceCopy } from 'utils/typography'
import './manager-proposal-section.scss'
import ManagerProposalDialogs from './manager-proposal-dialogs'
import {
  useEffect,
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Chip,
  Typography,
  Tooltip,
} from '@mui/material'
import moment from 'moment'
import {
  CheckCircle as CheckCircleIcon,
  Save as SaveIcon,
  Send as SendIcon,
  ExpandLess,
} from '@vedmarket/ui-kit'

import {
  APPLICATION_PROPOSAL_STATUS,
  COLORS,
} from 'config/constants'

import {
  canManagerEditProposalDraft,
  canManagerPublishProposal,
} from '../detail-visibility'
import ExecutorProposalCard from './executor-proposal-card'
import ProposalAccordion from './proposal-accordion'
import ManagerProposalHistory from './manager-proposal-history'
import ProposalServiceLinesEditor from './proposal-service-lines-editor'
import { defaultProposalCurrencyCode } from '../proposal-currencies'


const ManagerProposalSection = ({
  app,
  proposalForm,
  onProposalFormChange,
  runAction,
  onSaveProposal,
  onPublishProposal,
}: ApplicationManagerProposalSectionProps) => {
  const { t } = useTranslation()
  const [ publishProposalBlockedOpen, setPublishProposalBlockedOpen ] = useState(false)
  const [ publishProposalConfirmOpen, setPublishProposalConfirmOpen ] = useState(false)
  const [ proposalEditOpen, setProposalEditOpen ] = useState(true)

  const proposal = app.proposal
  const proposalCurrencyCode = defaultProposalCurrencyCode(app.original_calculation_currency)
  const isPublished = proposal?.status === APPLICATION_PROPOSAL_STATUS.PUBLISHED
  const isDeclined = Boolean(proposal?.declined_at)
  const canPublishProposal = canManagerPublishProposal(app)
  const canEditDraft = canManagerEditProposalDraft(app)
  const showPublishedState = isPublished && !isDeclined
  const showDraftStatus = !showPublishedState && !isDeclined
  const showPublishedEditorToggle = showPublishedState && canEditDraft
  const canShowEditor = proposalEditOpen && !isDeclined
  const showEditor = canShowEditor && canEditDraft
  const showProposalHead = showDraftStatus && Boolean(proposal?.status_label)

  useEffect(() => {
    if (proposal?.declined_at || !canEditDraft) {
      setProposalEditOpen(false)
      return
    }
    setProposalEditOpen(proposal?.status !== APPLICATION_PROPOSAL_STATUS.PUBLISHED)
  }, [ proposal?.status, proposal?.published_at, proposal?.declined_at, canEditDraft ])

  const handlePublishProposalClick = () => {
    if (!canPublishProposal) {
      setPublishProposalBlockedOpen(true)
      return
    }
    setPublishProposalConfirmOpen(true)
  }

  const handleConfirmPublishProposal = () => {
    setPublishProposalConfirmOpen(false)
    runAction(() => onPublishProposal(), 'applications__message__proposalSent')
  }

  const publishedAtLabel = proposal?.published_at
    ? moment(proposal.published_at).format('DD.MM.YYYY HH:mm')
    : null

  return (
    <ProposalAccordion hideHeading>
      <Box className='application-manager-proposal-section application-manager-proposal-section__manager-proposal' sx={{
        gap: 3,
      }}>
        {showProposalHead && (
          <Box className='application-manager-proposal-section__manager-proposal-head'>
            <Chip
              size='small'
              variant='outlined'
              label={<Typography component='span' variant='inherit'>{formatReferenceCopy(proposal?.status_label || '')}</Typography>}
            />
          </Box>
        )}

        {showPublishedState && (
          <>
            <Alert
              severity='success'
              icon={<CheckCircleIcon fontSize='inherit' style={{ color: COLORS.WHITE }} />}
              className='application-manager-proposal-section__published-banner'
              sx={{
                bgcolor: '#2E7D32',
                color: COLORS.WHITE,
                '& .MuiAlert-icon': { color: COLORS.WHITE },
                '& .MuiAlert-message': { color: COLORS.WHITE },
              }}
            >
              <Typography variant='body2' sx={{ color: COLORS.WHITE }}>
                {formatReferenceCopy(publishedAtLabel
                  ? t('applications__text__proposalPublishedBanner', { date: publishedAtLabel })
                  : t('applications__text__proposalPublishedBannerNoDate'))}
              </Typography>
            </Alert>
            {proposal && (
              <ExecutorProposalCard
                proposal={proposal}
                servicesComparison={app.proposal_services_comparison}
                managerView
              />
            )}
          </>
        )}

        {isDeclined && proposal && (
          <ExecutorProposalCard
            proposal={proposal}
            servicesComparison={app.proposal_services_comparison}
            declineReasonFallback={app.outcome_note}
            managerView
          />
        )}

        {showPublishedEditorToggle && !proposalEditOpen && (
          <Button
            variant='outlined'
            size='small'
            fullWidth
            onClick={() => setProposalEditOpen(true)}
          ><Typography component='span' variant='inherit'>
              {formatReferenceCopy(t('applications__text__proposalEditDraft'))}
            </Typography></Button>
        )}

        {showEditor && (
          <>
            {showPublishedState && (
              <Box className='application-manager-proposal-section__manager-proposal-head'>
                <Chip size='small' variant='outlined' label={(
                  <Typography component='span' variant='inherit'>
                    {formatReferenceCopy(t('applications__text__proposalDraftStatus'))}
                  </Typography>
                )} />
                <Typography variant='body2' color='text.primary'>
                  {formatReferenceCopy(t('applications__text__proposalEditDraftHint'))}
                </Typography>
              </Box>
            )}
            <ProposalServiceLinesEditor
              applicationPublicId={app.public_id}
              serviceLines={proposalForm.service_lines}
              currencyCode={proposalCurrencyCode}
              onServiceLinesChange={(serviceLines) => onProposalFormChange({
                ...proposalForm,
                currency_code: proposalCurrencyCode,
                service_lines: serviceLines,
              })}
            />
            <Box className='application-manager-proposal-section__actions'>
              {showPublishedState && (
                <Tooltip title={t('applications__text__proposalHideEditor')}>
                  <Button
                    variant='outlined'
                    aria-label={t('applications__text__proposalHideEditor')}
                    onClick={() => setProposalEditOpen(false)}
                    sx={{
                      minWidth: '238px',
                      flexShrink: 0,
                      '@container manager-proposal (max-width: 500px)': {
                        minWidth: '40px',
                        width: '40px',
                        paddingLeft: 0,
                        paddingRight: 0,
                      },
                    }}
                  >
                    <Box component='span' className='application-manager-proposal-section__collapse-icon'>
                      <ExpandLess width={24} height={24} />
                    </Box>
                    <Typography component='span' variant='inherit' className='application-manager-proposal-section__collapse-text'>
                      {formatReferenceCopy(t('applications__text__proposalHideEditor'))}
                    </Typography>
                  </Button>
                </Tooltip>
              )}
              <Button
                variant={showPublishedState ? 'contained' : 'outlined'}
                startIcon={<SaveIcon />}
                onClick={() => runAction(() => onSaveProposal(), 'applications__message__proposalDraftSaved')}
                sx={{
                  flex: showPublishedState ? 1 : undefined,
                  minWidth: showPublishedState ? undefined : '238px',
                  flexShrink: 0,
                }}
              ><Typography component='span' variant='inherit'>
                  {formatReferenceCopy(t('applications__text__saveProposal'))}
                </Typography></Button>
              {proposal?.status !== APPLICATION_PROPOSAL_STATUS.PUBLISHED && (
                <Button
                  variant='contained'
                  startIcon={<SendIcon />}
                  onClick={handlePublishProposalClick}
                  sx={{
                    flex: 1,
                    opacity: canPublishProposal ? undefined : 0.55,
                  }}
                ><Typography component='span' variant='inherit'>
                    {formatReferenceCopy(t('applications__text__publishProposal'))}
                  </Typography></Button>
              )}
            </Box>
          </>
        )}

        <ManagerProposalHistory events={app.events} />

        <ManagerProposalDialogs
          publishProposalConfirmOpen={publishProposalConfirmOpen}
          publishProposalBlockedOpen={publishProposalBlockedOpen}
          setPublishProposalConfirmOpen={setPublishProposalConfirmOpen}
          setPublishProposalBlockedOpen={setPublishProposalBlockedOpen}
          handleConfirmPublishProposal={handleConfirmPublishProposal}
        />
      </Box>
    </ProposalAccordion>
  )
}

export default ManagerProposalSection
